/**
 * Das Formular der Programmseite (`/radar/:id`) ohne React: Werte aus dem
 * Programm, Abgleich mit einem neueren Stand vom Server, Prüfung beim
 * Speichern.
 */

import { datum, nichtVor, type Fehler } from '@/lib/pruefen';
import type { RadarDeadlineKind, RadarProgram, RadarStatus } from '@/types';

export interface Formular {
  status: RadarStatus;
  note: string;
  deadline: string;
  deadline_kind: RadarDeadlineKind;
  event_date: string;
  event_end_date: string;
  review_at: string;
  manual_search_query: string;
}

export type FormularFeld = 'deadline' | 'event_date' | 'event_end_date' | 'review_at';

export function ausProgramm(p: RadarProgram): Formular {
  return {
    status: p.status,
    note: p.note ?? '',
    deadline: p.deadline ?? '',
    deadline_kind: p.deadline_kind,
    event_date: p.event_date ?? '',
    event_end_date: p.event_end_date ?? '',
    review_at: p.review_at ?? '',
    manual_search_query: p.manual_search_query ?? '',
  };
}

/**
 * Ein neuer Stand vom Server trifft auf ein Formular, in dem vielleicht schon
 * getippt wurde.
 *
 * Bis D2 ersetzte jeder neue Stand das ganze Formular. Im Dialog fiel das kaum
 * auf; auf der eigenen Seite stehen „Geprüft — geändert“ und die Notiz
 * nebeneinander: Wer eine Notiz anfängt und dann den Prüfauftrag erledigt,
 * hätte seinen Text verloren, denn die API schreibt den Befund in die Notiz
 * und liefert einen neuen Stand.
 *
 * Darum ein Abgleich zu dritt: Was seit dem letzten Stand (`basis`) hier
 * nicht angefasst wurde, kommt vom Server. Was hier geändert wurde, bleibt.
 * Die Notiz wächst auf dem Server nur hinten an (Befund, Radar-Vermerk); hat
 * beides sie geändert, bleibt der eigene Text und der neue Teil vom Server
 * kommt dahinter.
 */
export function zusammenfuehren(basis: Formular, lokal: Formular, server: Formular): Formular {
  const ergebnis = { ...server };
  for (const feld of Object.keys(server) as (keyof Formular)[]) {
    if (lokal[feld] === basis[feld]) continue;
    if (feld === 'note' && server.note !== basis.note && server.note.startsWith(basis.note)) {
      const neu = server.note.slice(basis.note.length).replace(/^\n+/, '');
      ergebnis.note = lokal.note.trim() ? `${lokal.note.replace(/\n+$/, '')}\n${neu}` : neu;
      continue;
    }
    (ergebnis as Record<keyof Formular, string>)[feld] = lokal[feld];
  }
  return ergebnis;
}

/** Steht im Formular etwas anderes als im geladenen Stand? */
export function geaendert(basis: Formular | null, lokal: Formular | null): boolean {
  if (!basis || !lokal) return false;
  return (Object.keys(basis) as (keyof Formular)[]).some((feld) => basis[feld] !== lokal[feld]);
}

/**
 * Prüfung beim Speichern. `unlesbar`: Datumsfelder mit halber Eingabe
 * (`unlesbareFelder` aus `lib/pruefen`), die der Browser als leer meldet.
 */
export function pruefen(f: Formular, unlesbar: Set<string> = new Set()): Fehler<FormularFeld> {
  const feld = (name: FormularFeld) => datum(f[name], { leerErlaubt: true, unlesbar: unlesbar.has(name) });
  return {
    deadline: feld('deadline'),
    event_date: feld('event_date'),
    event_end_date: feld('event_end_date') ?? nichtVor(f.event_end_date, f.event_date, 'Das Ende liegt vor dem Beginn'),
    review_at: feld('review_at'),
  };
}
